import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { PageHero } from '../../../components/pageHero/PageHero';
import { DragDropContext, Droppable, Draggable, DragUpdate, DropResult } from 'react-beautiful-dnd';
import { FixedSizeList, areEqual } from "react-window";
import DandD_Icon from "../../../images/DandD_Icon.png";
import { Spinner } from 'components/spinner/Spinner';
import { WondeApi } from 'api/wondeApi';
import WarningIcon from '../../../images/Icon_Warning.png';
import { useToast } from 'hooks/useToast';
import { PopupOrPanel } from '../../../components/popupsAndPanels/PopupAndPanels';
import {
  AUTOMATICALLY_SKIPPED_CODE,
  ClassesMisMatchPair,
  ClassesMisMatchResponse,
  MIS_CHANGE_NOT_FOUND_CODE,
  hasWondeMessageCode,
} from '../models';

type WIMisChangePairingProps = {
  setWondeIntegration: Dispatch<SetStateAction<boolean>>;
  onPairingComplete: () => void;
};

type PairingRecord = {
  id: number;
  className: string | null;
  applicableYear: string | null;
  keyStage: string | null;
  recordsType: "wonde" | "getset";
};

const NEW_MIS_LIST_ID = "droppable_new_mis_classes";
const GETSET_LIST_ID = "droppable_getset_classes";

export const WIMisChangePairing: React.FC<WIMisChangePairingProps> = ({
  setWondeIntegration,
  onPairingComplete,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [pairingData, setPairingData] = useState<ClassesMisMatchResponse | null>(null);
  const [pairs, setPairs] = useState<ClassesMisMatchPair[]>([]);
  const [hoveredGetSetClassId, setHoveredGetSetClassId] = useState<number | null>(null);

  const successToast = useToast("Successfully submitted");

  useEffect(() => {
    document.body.style.overflowY = '';
    loadData();
  }, []);

  const loadData = async () => {
    const result = await WondeApi.getClassesMisMatch();

    // Mis_Change_Not_Found means the pairing has already been completed, e.g. by another user
    if (
      hasWondeMessageCode(result.error?.formErrors, AUTOMATICALLY_SKIPPED_CODE)
      || hasWondeMessageCode(result.error?.formErrors, MIS_CHANGE_NOT_FOUND_CODE)
    ) {
      onPairingComplete();
      return;
    }

    if (result.error || !result.data) {
      setLoadError("An error occurred loading your classes, please try again.");
      setLoading(false);
      return;
    }

    const classes = result.data.classes ?? [];
    const wondeClasses = result.data.wondeClasses ?? [];

    setPairingData({ ...result.data, classes, wondeClasses });
    setPairs(
      classes
        .filter((item) => item.suggestedWondeClassesApplicableYearsId !== null
          && wondeClasses.some((wondeClass) => wondeClass.wondeClassesApplicableYearsId === item.suggestedWondeClassesApplicableYearsId))
        .map((item) => ({
          classesId: item.classesId,
          wondeClassesApplicableYearsId: item.suggestedWondeClassesApplicableYearsId as number,
        }))
    );
    setLoading(false);
  };

  const getSetRecords: PairingRecord[] = (pairingData?.classes ?? []).map((item) => ({
    id: item.classesId,
    className: item.className,
    applicableYear: item.applicableYear,
    keyStage: item.keyStage,
    recordsType: "getset",
  }));

  const newMisRecords: PairingRecord[] = (pairingData?.wondeClasses ?? []).map((item) => ({
    id: item.wondeClassesApplicableYearsId,
    className: item.className,
    applicableYear: item.applicableYear,
    keyStage: item.keyStage,
    recordsType: "wonde",
  }));

  const unpairedGetSetRecords = getSetRecords.filter((record) => !pairs.some((pair) => pair.classesId === record.id));
  const unpairedNewMisRecords = newMisRecords.filter((record) => !pairs.some((pair) => pair.wondeClassesApplicableYearsId === record.id));

  const pairedRecords = pairs.map((pair) => ({
    pair,
    newMisRecord: newMisRecords.find((record) => record.id === pair.wondeClassesApplicableYearsId),
    getSetRecord: getSetRecords.find((record) => record.id === pair.classesId),
  }));

  const onDragUpdate = (update: DragUpdate) => {
    if (update.destination?.droppableId === GETSET_LIST_ID) {
      setHoveredGetSetClassId(unpairedGetSetRecords[update.destination.index]?.id ?? null);
    } else {
      setHoveredGetSetClassId(null);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (result.destination?.droppableId === GETSET_LIST_ID && hoveredGetSetClassId !== null) {
      const wondeClassesApplicableYearsId = parseInt(result.draggableId);
      const classesId = hoveredGetSetClassId;

      setPairs((current) => [...current, { classesId, wondeClassesApplicableYearsId }]);
    }

    setHoveredGetSetClassId(null);
  };

  const unpair = (classesId: number) => {
    setPairs((current) => current.filter((pair) => pair.classesId !== classesId));
  };

  const submitPairs = async () => {
    setIsSubmitting(true);
    setError('');

    const result = await WondeApi.submitClassesMisMatch(pairs);

    if (!result.error) {
      successToast();
      setConfirmOpen(false);
      window.scrollTo(0, 0);
      onPairingComplete();
      return;
    }

    setError(
      hasWondeMessageCode(result.error.formErrors, MIS_CHANGE_NOT_FOUND_CODE)
        ? "Your classes have already been paired. Please cancel and sync again."
        : "An error occurred, please try again."
    );
    setIsSubmitting(false);
    setConfirmOpen(false);
    window.scrollTo(0, 0);
  };

  function getStyle(draggableStyle: any, virtualStyle: any, isDragging: boolean) {
    const combined = {
      ...virtualStyle,
      ...draggableStyle
    };

    const grid = 5;

    return {
      ...combined,
      height: isDragging ? combined.height : combined.height - grid,
      width: isDragging
        ? draggableStyle.width
        : `calc(${combined.width} - ${grid * 2}px)`,
      marginBottom: grid
    };
  }

  const Item = ({ provided, item, style, isDragging }: { provided: any; item: PairingRecord; style: any; isDragging: boolean }) => {
    const highlight = isDragging || (item.recordsType === "getset" && hoveredGetSetClassId === item.id);

    return (
      <div
        className={"RecordBlock" + (highlight ? " Highlight" : "")}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        ref={provided.innerRef}
        style={getStyle(provided.draggableProps.style, style, isDragging)}
      >
        <div className="Inner">
          <div className="RBCol Col1">
            {item.recordsType === "wonde" ?
              <div className="IconAndTxt">
                <div className="RBIcon"><img src={DandD_Icon} alt={item.className ?? ''} /></div>
                <div className="RBTxt">{item.className}</div>
              </div>
              :
              item.className
            }
          </div>
          <div className="RBCol Col2">{item.applicableYear}</div>
          <div className="RBCol Col3">{item.keyStage}</div>
        </div>
      </div>
    );
  };

  const Row = React.memo(function Row(props: any) {
    const { data: items, index, style } = props;
    const item: PairingRecord | undefined = items[index];

    if (!item) {
      return null;
    }

    return (
      <Draggable
        key={item.recordsType + "_" + item.id}
        draggableId={item.recordsType === "wonde" ? item.id.toString() : "getset_" + item.id}
        index={index}
        isDragDisabled={item.recordsType === "getset"}
      >
        {(provided, snapshot) => (
          <Item provided={provided} item={item} style={style} isDragging={snapshot.isDragging} />
        )}
      </Draggable>
    );
  }, areEqual);

  const recordColumns = [
    {
      droppableId: NEW_MIS_LIST_ID,
      columnName: `Unpaired classes from ${pairingData?.newMis ?? "your new MIS"}`,
      records: unpairedNewMisRecords,
    },
    {
      droppableId: GETSET_LIST_ID,
      columnName: "Unpaired Get Set 4 Education classes",
      records: unpairedGetSetRecords,
    },
  ];

  const recordHeaderRow = (
    <div className="LCHdrRow">
      <div className="LCHdr Col1">Class name</div>
      <div className="LCHdr Col2">Year group</div>
      <div className="LCHdr Col3">Key stage</div>
    </div>
  );

  const pairedRecordBlock = (record: PairingRecord | undefined) => (
    <div className="RRDetails">
      <div className="RecordBlock">
        <div className="Inner">
          <div className="RBCol Col1">{record?.className}</div>
          <div className="RBCol Col2">{record?.applicableYear}</div>
          <div className="RBCol Col3">{record?.keyStage}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="PageContent WondeIntegrationPage Step2 MisChangePairing">
      <PageHero title="Pair Your Classes" />
      <div className="WidthContent">
        <div className="TopInfoRow">
          <div className="LeftSide">
            <h2>Your School Has Changed MIS</h2>
          </div>
          <div className="RightSide"></div>
        </div>

        {pairingData &&
          <>
            <p>
              {pairingData.previousMis
                ? `Your school has moved from ${pairingData.previousMis} to ${pairingData.newMis}. `
                : `Your school has moved to ${pairingData.newMis}. `}
              This is a whole-school change: your new MIS has given every class a new ID, so each of your Get Set 4 Education classes needs pairing with the same class in your new MIS. Your pupils and assessment data stay with your Get Set 4 Education classes.
            </p>
            <p>
              We have paired the classes where the class name and year group match. Check these pairs, unpair any that are wrong, and drag the remaining classes from your new MIS onto the Get Set 4 Education class they belong to.
            </p>
            <p>
              You can confirm with some or all classes left unpaired. They are not deleted, and will show later in the sync as unmatched classes.
            </p>
          </>
        }

        {error &&
          <div className="ErrorTxt">{error}</div>
        }

        {loading ?
          <div className="LoadingPanel">
            <div className="LPInfo">
              <Spinner color={'#f59e16'} />
              <div className="LPTxt">Hang tight...we are just checking your data!</div>
            </div>
          </div>
          :
          <>
            {loadError ?
              <div className="ErrorPageStatus">
                <img src={WarningIcon} alt="Error loading classes" className="WondeLogo" />
                <div className="Txt">{loadError}</div>
              </div>
              :
              <>
                {isSubmitting &&
                  <div className="WILoadingPanel">
                    <Spinner color={'#f59e16'} />
                  </div>
                }

                <div className="RecordListColumns">
                  <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                    {recordColumns.map((column) => (
                      <div className="ListCol" key={column.droppableId}>
                        <h4>{column.columnName}</h4>

                        {column.records.length > 0 ?
                          <>
                            {recordHeaderRow}

                            <div className="RecordsListContainer">
                              <Droppable
                                droppableId={column.droppableId}
                                mode="virtual"
                                renderClone={(provided, snapshot, rubric) => (
                                  <Item
                                    provided={provided}
                                    isDragging={snapshot.isDragging}
                                    item={column.records[rubric.source.index]}
                                    style={null}
                                  />
                                )}
                              >
                                {(provided, snapshot) => (
                                  <FixedSizeList
                                    height={440}
                                    itemCount={snapshot.isUsingPlaceholder ? column.records.length + 1 : column.records.length}
                                    itemSize={45}
                                    width={"100%"}
                                    outerRef={provided.innerRef}
                                    itemData={column.records}
                                  >
                                    {Row}
                                  </FixedSizeList>
                                )}
                              </Droppable>
                            </div>
                          </>
                          :
                          <p>There are no classes.</p>
                        }
                      </div>
                    ))}
                  </DragDropContext>
                </div>

                <div className="LineSpacer4"></div>
                <div className="LineSpacer5"></div>

                <h4>Paired Classes</h4>
                {pairedRecords.length <= 0 &&
                  <div className="NoMatchedRecordsPanel">
                    <div className="EmptyMsgBlock">
                      <div className="Inner">
                        <p>No classes paired</p>
                      </div>
                    </div>
                  </div>
                }

                {pairedRecords.length > 0 &&
                  <div className="MatchedRecordsPanel">
                    <div className="SelectedRecordRow TopRow">
                      <div className="SRCol Col1">
                        <div className="RRDetails">{recordHeaderRow}</div>
                      </div>
                      <div className="SRCol Col2"></div>
                      <div className="SRCol Col3">
                        <div className="RRDetails">{recordHeaderRow}</div>
                      </div>
                      <div className="SRCol Col4"></div>
                    </div>

                    <div className="MRInner">
                      {pairedRecords.map((item) => (
                        <div className="SelectedRecordRow" key={item.pair.classesId}>
                          <div className="SRCol Col1">{pairedRecordBlock(item.newMisRecord)}</div>
                          <div className="SRCol Col2"></div>
                          <div className="SRCol Col3">{pairedRecordBlock(item.getSetRecord)}</div>
                          <div className="SRCol Col4">
                            <div className="Btn BtnWhiteOrange" onClick={() => unpair(item.pair.classesId)}>Unpair</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                }
              </>
            }

            <div className="BtmBtns">
              <div className="Btn BtnWhite LeftBtn" onClick={() => {
                window.scrollTo(0, 0);
                setWondeIntegration(false);
              }}>Cancel</div>
              {!loadError &&
                <div className="Btn BtnOrange RightBtn" onClick={() => setConfirmOpen(true)}>Confirm</div>
              }
            </div>
          </>
        }
      </div>

      {confirmOpen &&
        <PopupOrPanel
          isPopup={true}
          isOpen={confirmOpen}
          hdrTitle="Confirm Class Pairing"
          position="right"
          givenClass="WondeMisChangePairing"
          primaryBtnTxt="Confirm"
          secondaryBtnTxt="Cancel"
          handleClose={() => !isSubmitting && setConfirmOpen(false)}
          handlePrimaryBtn={() => !isSubmitting && submitPairs()}
          handleSecondaryBtn={() => !isSubmitting && setConfirmOpen(false)}
          width={460}
          isSubmitting={isSubmitting}
        >
          <p>
            {pairs.length} of your {getSetRecords.length} Get Set 4 Education {getSetRecords.length === 1 ? "class is" : "classes are"} paired.
          </p>
          {unpairedGetSetRecords.length > 0 &&
            <p>
              {unpairedGetSetRecords.length} {unpairedGetSetRecords.length === 1 ? "class is" : "classes are"} not paired and will show later in the sync as unmatched classes.
            </p>
          }
          <p>You cannot change the pairing once it is confirmed.</p>
        </PopupOrPanel>
      }
    </div>
  );
};
